import { USER_ROLES } from "./constants";
import {
  DetailedUser,
  UserRole,
  UserSaveResponse,
  UserSaveResult,
} from "./user";

export const getEnrichedUser = (user: DetailedUser) => ({
  ...user,
  role: user.role.toUpperCase() as UserRole,
});

export const getUserSaveResult = (
  data: UserSaveResponse,
  status: number,
): UserSaveResult => ({
  user: getEnrichedUser("user" in data ? data.user : data),
  welcomeEmailFailed: status === 202,
});

export const getAccountUsers = (users: DetailedUser[]) =>
  users.filter(
    (user) => user.role !== USER_ROLES.ADMIN && user.role !== USER_ROLES.TECH,
  );

export const isAdmin = (role: string | undefined): boolean =>
  role?.toUpperCase() === USER_ROLES.ADMIN;

export const isOwner = (role: string | undefined): boolean =>
  role?.toUpperCase() === USER_ROLES.OWNER;

export const isManager = (role: string | undefined): boolean =>
  role?.toUpperCase() === USER_ROLES.MANAGER;

export const isRep = (role: string | undefined): boolean =>
  role?.toUpperCase() === USER_ROLES.REP;

export const isTech = (role: string | undefined): boolean =>
  role?.toUpperCase() === USER_ROLES.TECH;

export const isApOnly = (role: string | undefined): boolean =>
  role?.toUpperCase() === USER_ROLES.AP_ONLY;

export const isAdminOrOwner = (role: string | undefined): boolean =>
  isAdmin(role) || isOwner(role);

/**
 * Mirrors `User#can_manage_finances?` on the API: admins, owners, and anyone
 * who belongs to the tenant, whatever their role there.
 */
export const canManageFinances = (
  user: { role?: string; parentable_type?: string } | null | undefined,
): boolean =>
  !!user &&
  (isAdminOrOwner(user.role) ||
    user.parentable_type?.toUpperCase() === "TENANT");

export const isAdminOrOwnerOrManager = (role: string | undefined): boolean =>
  isAdmin(role) || isOwner(role) || isManager(role);

/**
 * Whether this user may read and answer technician pay questions (PackChat).
 *
 * Mirrors `User#may_answer_pack_questions?` on the API, which is what actually enforces
 * it — this only decides whether to offer the tab.
 *
 * Tenant-parented users are included for the same reason the API includes them in
 * `can_manage_finances?`: a franchise or parent-org user administers the business
 * whatever role they hold under the tenant, and leaving them out locked the only person
 * covering that business out of every question. Technicians are excluded FIRST, because
 * a technician parented to a Tenant would otherwise pass on membership alone.
 */
export const mayAnswerPackQuestions = (
  user: { role?: string; parentable_type?: string } | null | undefined,
): boolean => {
  if (!user?.role || isTech(user.role)) return false;

  return (
    isAdminOrOwnerOrManager(user.role) ||
    user.parentable_type?.toUpperCase() === "TENANT"
  );
};
